import { truncate } from 'lodash'
import { useRouter } from 'next/router'
import React, { useLayoutEffect } from 'react'
import { EXPERT_DETAIL_TAB_KEYS } from 'shared-constants'
import { AuthModal } from 'components/AuthModal'
import { Error } from 'components/Error'
import { useClientFavoriteExpertsManagement } from 'components/ExpertCard'
import { Flex } from 'components/Layout'
import { LinkWithText } from 'components/Link'
import { PureSSRIndicator } from 'components/PureSSRIndicator'
import { Seo } from 'components/Seo'
import { Text } from 'components/Typography'
import { WrappedLoader } from 'components/WrappedLoader'
import { EXPERT_DETAIL_TAB_KEY_ROUTES, ROUTE } from 'constants/routes'
import { useUserAuth } from 'providers/userAuth'
import { USER_TYPE } from 'providers/userAuthUtil'
import { FONT_SIZE, SPACE } from 'Theme'
import { useMedia } from 'useMedia'
import {
  formatExpertDetailTitle,
  formatPageTitleWithDash,
} from 'utils/formatTitle'
import { silentPushUrl } from 'utils/pushUrl'
import { ExpertTabs } from './ExpertTabs'
import { Header } from './Header'
import { useExpertDetailRedirect } from './hooks'
import { convertToPlaintext } from './utils/markdownToText'

interface ExpertDetailTabWrapperProps {
  slug: string
  expertId: number
  currentTab: (typeof EXPERT_DETAIL_TAB_KEYS)[keyof typeof EXPERT_DETAIL_TAB_KEYS]
}

export const ExpertDetailTabWrapper: React.FC<
  React.PWC<ExpertDetailTabWrapperProps>
> = ({ slug, expertId, children, currentTab }) => {
  const router = useRouter()
  const { isReady } = useMedia()
  const { isClientSignedIn, user } = useUserAuth()

  const {
    expert,
    availableExpertDetailTabs: availableTabs,
    isLoading,
  } = useExpertDetailRedirect({
    expertId,
    currentTab,
    router,
  })

  const {
    addExpertToFavorites,
    isAuthModalOpen,
    isLoading: isFavoriteExpertLoading,
  } = useClientFavoriteExpertsManagement({
    clientId: isClientSignedIn && user ? user.id : null,
  })

  // Fixes the issue with the page not scrolling to the session listing when the page is loaded with a hash due to dynamic rendering
  useLayoutEffect(() => {
    if (
      typeof window !== 'undefined' &&
      expert &&
      availableTabs.length > 0 &&
      !isLoading &&
      isReady
    ) {
      const node = document.getElementById(window.location.hash.slice(1))

      if (node) {
        node.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [expert, availableTabs, isLoading, isReady])

  if (!expert && isLoading) {
    return <WrappedLoader minHeight="100vh" />
  }

  if (!expert && availableTabs.length === 0 && !isLoading) {
    return (
      <Error
        description={
          <Text fontSize={FONT_SIZE.PX_16}>
            Try searching again or choose Experts in one of the{' '}
            <LinkWithText
              fontSize={FONT_SIZE.PX_16}
              href={ROUTE.FIND_AN_EXPERT()}
            >
              experts
            </LinkWithText>
          </Text>
        }
      >
        We couldn&apos;t find the Expert you&apos;re looking for
      </Error>
    )
  }

  const isConsultationTabAvailable = availableTabs.includes(
    EXPERT_DETAIL_TAB_KEYS.CONSULTATION
  )

  const currentTabRoute = EXPERT_DETAIL_TAB_KEY_ROUTES[currentTab]({
    slug: expert.slug,
  })

  const title = formatExpertDetailTitle(expert.profileName)

  const seoProps = {
    title,
    description: truncate(convertToPlaintext(expert.longDescription), {
      length: 200,
    }),
    metaTitle: formatPageTitleWithDash(title),
    image: expert.headshotImageAttachment?.url,
    urlPath: currentTabRoute,
  }

  return (
    <>
      <Seo {...seoProps} />
      <Flex
        mb={SPACE.PX_60}
        mt={SPACE.PX_40}
        flexDirection="column"
        alignItems="center"
      >
        <Header
          id={expert.id}
          route={currentTabRoute}
          slug={expert.slug}
          images={expert.images}
          hasSessions={isConsultationTabAvailable}
          headshotImageAttachment={expert.headshotImageAttachment}
          profileName={expert.profileName}
          shortDescription={expert.shortDescription}
          instagramAccountUrl={expert.instagramAccountUrl}
          primaryLocation={expert.primaryLocation}
          city={expert.city}
          countryIsoCode={expert.countryIsoCode}
        />
        {availableTabs.length > 1 && (
          <ExpertTabs
            slug={slug}
            tabs={
              availableTabs as Array<
                (typeof EXPERT_DETAIL_TAB_KEYS)[keyof typeof EXPERT_DETAIL_TAB_KEYS]
              >
            }
            defaultTab={currentTab}
          />
        )}
        {children}
      </Flex>
      <AuthModal
        userAuthType={USER_TYPE.CLIENT}
        isLoading={isFavoriteExpertLoading}
        onModalClose={() => {
          silentPushUrl({
            router,
            route: currentTabRoute,
          })
        }}
        isModalOpen={isAuthModalOpen}
        onSignInCompleted={async () => {
          silentPushUrl({
            router,
            route: currentTabRoute,
          })
          await addExpertToFavorites({
            expertId: expert.id,
          })
        }}
      />
      <PureSSRIndicator />
    </>
  )
}
